import { createRoute, redirect, Link } from '@tanstack/react-router'
import { rootRoute } from './__root'
import { apiClient } from '@/lib/api'
import { AppHeader } from '@/components/AppHeader'
import { useAuth } from '@/hooks/useAuth'
import {
  useTeam,
  useCreateTeam,
  useUpdateTeam,
  useInviteTeamMember,
  useRemoveTeamMember,
  type TeamMember,
} from '@/hooks/useTeam'
import { useState } from 'react'
import { FaTrash as Trash } from 'react-icons/fa'
import { APIError } from '@/lib/api'

export const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/team',
  beforeLoad: async () => {
    try {
      await apiClient.get('/api/v1/auth/me')
    } catch {
      throw redirect({ to: '/login' })
    }
  },
  component: TeamPage,
})

function TeamPage() {
  const { user } = useAuth()
  const teamId = user?.teamId
  const { data: team, isLoading } = useTeam(teamId)

  const isStudio = user?.tier === 'studio'

  return (
    <div className="min-h-[100dvh] bg-surface-base">
      <AppHeader title="Team" subtitle="Manage your studio team" />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {!isStudio ? (
          <NoStudioPlan />
        ) : isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
          </div>
        ) : !teamId || !team ? (
          <CreateTeamPrompt />
        ) : (
          <TeamDetails team={team} currentUserId={user?.id ?? ''} />
        )}
      </main>
    </div>
  )
}

function NoStudioPlan() {
  return (
    <div className="bg-surface-raised rounded-xl shadow-low p-8 border border-brd-subtle text-center">
      <h2 className="text-lg font-semibold text-content-primary mb-2">
        Studio plan required
      </h2>
      <p className="text-sm text-content-secondary mb-4">
        Team management is available on the Studio plan. Upgrade to create a
        team and share layouts with your studio members.
      </p>
      <Link to="/home" className="btn-primary btn-md">
        Back to Home
      </Link>
    </div>
  )
}

function CreateTeamPrompt() {
  const createTeam = useCreateTeam()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setError(null)
    try {
      await createTeam.mutateAsync({ name: name.trim() })
    } catch (err) {
      setError(
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to create team',
      )
    }
  }

  return (
    <div className="bg-surface-raised rounded-xl shadow-low p-8 border border-brd-subtle">
      <h2 className="text-lg font-semibold text-content-primary mb-2">
        Create your team
      </h2>
      <p className="text-sm text-content-secondary mb-6">
        Set up a studio team so your members can access all shared layouts
        automatically.
      </p>
      <form onSubmit={handleCreate} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-1">
            Team name
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Sunset Sound Studios"
            className="w-full px-3 py-2 rounded-lg border border-brd-subtle bg-surface-sunken text-content-primary placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-brd-emphasis"
            autoFocus
          />
        </div>
        {error && (
          <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={createTeam.isPending || !name.trim()}
          className="w-full btn-primary"
        >
          {createTeam.isPending ? 'Creating...' : 'Create Team'}
        </button>
      </form>
    </div>
  )
}

function TeamDetails({
  team,
  currentUserId,
}: {
  team: NonNullable<ReturnType<typeof useTeam>['data']>
  currentUserId: string
}) {
  const isOwner = team.ownerUserId === currentUserId
  const currentMember = team.members.find(m => m.userId === currentUserId)
  const isAdmin = isOwner || currentMember?.role === 'admin'

  return (
    <div className="space-y-6">
      <TeamNameCard team={team} canEdit={isAdmin} />
      <MemberList
        team={team}
        currentUserId={currentUserId}
        isOwner={isOwner}
        isAdmin={isAdmin}
      />
      {isAdmin && <InviteMemberCard teamId={team.teamId} />}
      {!isOwner && (
        <LeaveTeamCard teamId={team.teamId} currentUserId={currentUserId} />
      )}
    </div>
  )
}

function TeamNameCard({
  team,
  canEdit,
}: {
  team: NonNullable<ReturnType<typeof useTeam>['data']>
  canEdit: boolean
}) {
  const updateTeam = useUpdateTeam(team.teamId)
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(team.name)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setError(null)
    try {
      await updateTeam.mutateAsync({ name: name.trim() })
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename')
    }
  }

  return (
    <div className="bg-surface-raised rounded-xl shadow-low p-6 border border-brd-subtle">
      {editing ? (
        <form onSubmit={handleSave} className="space-y-3">
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-brd-emphasis bg-surface-sunken text-content-primary focus:outline-none focus:ring-2 focus:ring-accent/50 text-lg font-semibold"
            autoFocus
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={updateTeam.isPending}
              className="btn-primary btn-sm"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false)
                setName(team.name)
              }}
              className="btn-secondary btn-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-content-primary">
              {team.name}
            </h2>
            <p className="text-xs text-content-tertiary mt-0.5">
              Created {new Date(team.createdAt).toLocaleDateString()}
              {' \u00b7 '}
              {team.members.length}{' '}
              {team.members.length === 1 ? 'member' : 'members'}
            </p>
          </div>
          {canEdit && (
            <button
              onClick={() => setEditing(true)}
              className="btn-secondary btn-sm"
            >
              Rename
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function MemberList({
  team,
  currentUserId,
  isOwner,
  isAdmin,
}: {
  team: NonNullable<ReturnType<typeof useTeam>['data']>
  currentUserId: string
  isOwner: boolean
  isAdmin: boolean
}) {
  const removeMember = useRemoveTeamMember(team.teamId)
  const [error, setError] = useState<string | null>(null)

  const handleRemove = async (member: TeamMember) => {
    setError(null)
    try {
      await removeMember.mutateAsync(member.userId)
    } catch (err) {
      setError(
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to remove member',
      )
    }
  }

  const canRemove = (member: TeamMember) => {
    if (member.role === 'owner') return false
    if (member.userId === currentUserId) return false
    return isOwner || isAdmin
  }

  const roleLabel = (role: string) => {
    if (role === 'owner') return 'Owner'
    if (role === 'admin') return 'Admin'
    return 'Member'
  }

  const roleBadgeClass = (role: string) => {
    if (role === 'owner') return 'bg-[color:var(--second-soft)] text-second'
    if (role === 'admin') return 'bg-accent-muted text-accent'
    return 'bg-surface-sunken text-content-secondary'
  }

  return (
    <div className="bg-surface-raised rounded-xl shadow-low border border-brd-subtle">
      <div className="px-6 py-4 border-b border-brd-subtle">
        <h3 className="text-sm font-semibold text-content-primary">Members</h3>
      </div>
      {error && (
        <div className="mx-6 mt-3 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      <ul className="divide-y divide-brd-subtle">
        {team.members.map(member => (
          <li
            key={member.userId}
            className="flex items-center justify-between px-6 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm text-content-primary truncate">
                  {member.email}
                </span>
                {member.userId === currentUserId && (
                  <span className="text-[10px] text-content-tertiary">
                    (you)
                  </span>
                )}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${roleBadgeClass(member.role)}`}
                >
                  {roleLabel(member.role)}
                </span>
              </div>
              <div className="text-[11px] text-content-tertiary">
                Joined {new Date(member.joinedAt).toLocaleDateString()}
              </div>
            </div>
            {canRemove(member) && (
              <button
                onClick={() => handleRemove(member)}
                disabled={removeMember.isPending}
                className="p-1.5 rounded text-content-tertiary hover:text-destructive hover:bg-destructive/5 transition-colors duration-150"
                title="Remove member"
              >
                <Trash size={12} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function InviteMemberCard({ teamId }: { teamId: string }) {
  const invite = useInviteTeamMember(teamId)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'member' | 'admin'>('member')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setError(null)
    setNotice(null)
    const submittedEmail = email.trim()
    try {
      await invite.mutateAsync({ email: submittedEmail, role })
      setNotice(`${submittedEmail} added to the team.`)
      setEmail('')
    } catch (err) {
      setError(
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to invite member',
      )
    }
  }

  return (
    <div className="bg-surface-raised rounded-xl shadow-low p-6 border border-brd-subtle">
      <h3 className="text-sm font-semibold text-content-primary mb-3">
        Add a team member
      </h3>
      <form onSubmit={handleInvite} className="space-y-3">
        <input
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="colleague@studio.com"
          className="w-full px-3 py-2 rounded-lg border border-brd-subtle bg-surface-sunken text-content-primary placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-brd-emphasis text-sm"
        />
        <div className="flex gap-2">
          {(['member', 'admin'] as const).map(r => (
            <button
              type="button"
              key={r}
              onClick={() => setRole(r)}
              className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors duration-150 ${
                role === r
                  ? 'border-accent bg-accent-muted text-accent'
                  : 'border-brd-subtle text-content-secondary hover:bg-surface-sunken'
              }`}
            >
              {r === 'member' ? 'Member' : 'Admin'}
            </button>
          ))}
        </div>
        {error && (
          <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        {notice && !error && (
          <div className="text-xs text-success bg-success/5 border border-success/20 rounded-md px-3 py-2">
            {notice}
          </div>
        )}
        <button
          type="submit"
          disabled={invite.isPending || !email.trim()}
          className="w-full btn-primary"
        >
          {invite.isPending ? 'Adding...' : 'Add Member'}
        </button>
        <p className="text-[10px] text-content-tertiary">
          The user must have an existing account. They&apos;ll automatically
          gain access to all team layouts.
        </p>
      </form>
    </div>
  )
}

function LeaveTeamCard({
  teamId,
  currentUserId,
}: {
  teamId: string
  currentUserId: string
}) {
  const removeMember = useRemoveTeamMember(teamId)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLeave = async () => {
    setError(null)
    try {
      await removeMember.mutateAsync(currentUserId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to leave team')
      setConfirming(false)
    }
  }

  return (
    <div className="bg-surface-raised rounded-xl shadow-low p-6 border border-brd-subtle">
      <h3 className="text-sm font-semibold text-content-primary mb-2">
        Leave team
      </h3>
      <p className="text-xs text-content-tertiary mb-3">
        You&apos;ll lose access to all team layouts. This can&apos;t be undone.
      </p>
      {error && (
        <div className="mb-3 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      {confirming ? (
        <div className="flex gap-2">
          <button
            onClick={handleLeave}
            disabled={removeMember.isPending}
            className="btn btn-sm bg-destructive text-white hover:bg-destructive/90 disabled:opacity-50"
          >
            {removeMember.isPending ? 'Leaving...' : 'Confirm leave'}
          </button>
          <button
            onClick={() => setConfirming(false)}
            className="btn-secondary btn-sm"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="btn-danger btn-sm"
        >
          Leave this team
        </button>
      )}
    </div>
  )
}
